/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GenAILiveClient } from '../../lib/genai-live-client';
import { LiveConnectConfig, LiveServerToolCall } from '@google/genai';
import { AudioStreamer } from '../../lib/audio-streamer';
import { audioContext } from '../../lib/utils';
import VolMeterWorket from '../../lib/worklets/vol-meter';
// Fix: Use relative path for lib/state
import { useLogStore, useConnectionStore } from '../../lib/state';

export type UseLiveApiResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig) => void;
  config: LiveConnectConfig;

  connect: () => Promise<void>;
  disconnect: () => void;
  connected: boolean;

  apiKey: string;
  volume: number;
};

export function useLiveApi({
  apiKey,
}: {
  apiKey: string;
}): UseLiveApiResults {
  const client = useMemo(() => new GenAILiveClient(apiKey), [apiKey]);
  const { setConnected: setGlobalConnected } = useConnectionStore();

  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [volume, setVolume] = useState(0);
  const [connected, setConnected] = useState(false);
  const [config, setConfig] = useState<LiveConnectConfig>({});

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: 'audio-out' }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>('vumeter-out', VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          })
          .catch(err => {
            console.error('Error adding worklet:', err);
          });
      });
    }
  }, [audioStreamerRef]);

  useEffect(() => {
    const onOpen = () => {
      console.log('Live API: Connection opened');
      setConnected(true);
      setGlobalConnected(true);
    };

    const onClose = () => {
      console.log('Live API: Connection closed');
      setConnected(false);
      setGlobalConnected(false);
    };

    const stopAudioStreamer = () => {
      if (audioStreamerRef.current) {
        audioStreamerRef.current.stop();
      }
    };

    const onAudio = (data: ArrayBuffer) => {
      console.log('Audio data received from AI:', data.byteLength, 'bytes');
      if (audioStreamerRef.current) {
        // Ensure audio context is resumed for playback
        if (audioStreamerRef.current.context.state === 'suspended') {
          console.log('Resuming audio context for playback...');
          audioStreamerRef.current.context.resume().then(() => {
            console.log('Audio context resumed successfully');
            audioStreamerRef.current?.addPCM16(new Uint8Array(data));
          }).catch(err => {
            console.error('Failed to resume audio context:', err);
          });
        } else {
          // Resume audio context if needed
          audioStreamerRef.current.resume().then(() => {
            audioStreamerRef.current?.addPCM16(new Uint8Array(data));
          }).catch(err => {
            console.error('Failed to resume audio:', err);
          });
        }
      } else {
        console.warn('AudioStreamer not available for playback');
      }
    };

    // Bind event listeners
    client.on('open', onOpen);
    client.on('close', onClose);
    client.on('interrupted', stopAudioStreamer);
    client.on('audio', onAudio);

    const onToolCall = (toolCall: LiveServerToolCall) => {
      const functionResponses: any[] = [];

      for (const fc of toolCall.functionCalls) {
        functionResponses.push({
          id: fc.id,
          name: fc.name,
          response: { result: 'ok' },
        });
      }

      client.sendToolResponse({ functionResponses: functionResponses });
    };

    // Add recursion guards to prevent infinite event loops
    let isEmittingInput = false;
    let isEmittingOutput = false;
    let isEmittingContent = false;

    const onInputTranscription = (text: string, isFinal: boolean) => {
      if (isEmittingInput) return;
      isEmittingInput = true;
      
      try {
        console.log('Live API: Input transcription received:', text, isFinal);
        client.emitter.emit('inputTranscription', text, isFinal);
      } finally {
        setTimeout(() => {
          isEmittingInput = false;
        }, 50);
      }
    };

    const onOutputTranscription = (text: string, isFinal: boolean) => {
      if (isEmittingOutput) return;
      isEmittingOutput = true;
      
      try {
        console.log('Live API: Output transcription received:', text, isFinal);
        client.emitter.emit('outputTranscription', text, isFinal);
      } finally {
        setTimeout(() => {
          isEmittingOutput = false;
        }, 50);
      }
    };

    const onContent = (content: any) => {
      if (isEmittingContent) return;
      isEmittingContent = true;
      
      try {
        console.log('Live API: Content received:', content);
        client.emitter.emit('content', content);
      } finally {
        setTimeout(() => {
          isEmittingContent = false;
        }, 50);
      }
    };

    client.on('toolcall', onToolCall);
    client.on('inputTranscription', onInputTranscription);
    client.on('outputTranscription', onOutputTranscription);
    client.on('content', onContent);

    return () => {
      // Clean up event listeners
      client.off('open', onOpen);
      client.off('close', onClose);
      client.off('interrupted', stopAudioStreamer);
      client.off('audio', onAudio);
      client.off('toolcall', onToolCall);
      client.off('inputTranscription', onInputTranscription);
      client.off('outputTranscription', onOutputTranscription);
      client.off('content', onContent);
    };
  }, [client, setGlobalConnected]);

  const connect = useCallback(async () => {
    if (!config) {
      throw new Error('config has not been set');
    }
    
    console.log('Attempting to connect to Gemini Live API...');
    
    // Only disconnect if we're already connected
    if (client.status === 'connected') {
      console.log('Disconnecting existing connection...');
      client.disconnect();
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait longer for cleanup
    }
    
    // Wait a bit before connecting to prevent rapid reconnection
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      const result = await client.connect(config);
      console.log('Connection result:', result);
      
      if (result) {
        console.log('Connection established successfully');
        setConnected(true);
        setGlobalConnected(true);
        
        // Send initial message to keep connection alive
        setTimeout(() => {
          if (client.status === 'connected') {
            console.log('Sending initial message to maintain connection...');
            try {
              client.send({
                text: "Hello, I'm ready to help you."
              });
            } catch (error) {
              console.log('Initial message send error:', error);
            }
          }
        }, 2000);
        
        // Add a stability check - wait longer to ensure connection is stable
        setTimeout(() => {
          if (client.status === 'connected') {
            console.log('Connection is stable');
          } else {
            console.log('Connection lost after initial establishment');
            setConnected(false);
            setGlobalConnected(false);
          }
        }, 5000); // Increased timeout
      } else {
        console.log('Connection failed - result was false');
        setConnected(false);
        setGlobalConnected(false);
      }
      
      return result;
    } catch (error) {
      console.error('Connection error:', error);
      setConnected(false);
      setGlobalConnected(false);
      throw error;
    }
  }, [client, config, setConnected, setGlobalConnected]);

  const disconnect = useCallback(async () => {
    try {
      client.disconnect();
      setConnected(false);
      setGlobalConnected(false);
      // Wait a bit to ensure connection is fully closed
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error during disconnect:', error);
      setConnected(false);
      setGlobalConnected(false);
    }
  }, [setConnected, client, setGlobalConnected]);

  return {
    client,
    config,
    setConfig,
    connect,
    connected,
    disconnect,
    apiKey,
    volume,
  };
}
